// Firebase Analytics wrapper for the competitions web app.
//
// Mirrors the dual-track / PII-stripping pattern from the mobile app
// (clubbie-mobile/redux/actions/googleAnalyticsActions.js) but only writes to
// Firebase Analytics — Segment is mobile-only. Events land in the same GA4
// property as mobile (Firebase project `clubbie-5529d`) so admin web and
// member mobile activity unifies in one dashboard.
//
// All events include `app: 'competitions-web'` as a default param so the GA4
// console can distinguish web-from-the-tournament-admin-app traffic vs any
// other web property that ever points at the same Firebase project.

import { logEvent as firebaseLogEvent, setUserId, setUserProperties } from 'firebase/analytics';

import { getAnalyticsInstance } from './firebase.utils';
import {
  segmentTrack,
  segmentIdentify,
  segmentReset,
  segmentPage,
} from './segmentClient';
import {
  LOGIN_SUCCESS,
  LOGOUT,
  PASSWORD_RESET_REQUESTED,
  PASSWORD_RESET_COMPLETED,
  SIGNUP_COMPLETED,
  ORGANISATION_CREATED,
  ORGANISATION_MEMBER_ADDED,
  ORGANISATION_MEMBER_REMOVED,
  COMPETITION_CREATED,
  TOURNAMENT_WIZARD_STEP_COMPLETED,
  PARTICIPANT_ADDED,
  FORMAT_CREATED,
  FIXTURES_GENERATED,
  FIXTURE_CREATED,
  FIXTURE_EDITED,
  RESULT_RECORDED,
  TEAM_REGISTRATION_PAID,
  PUBLIC_VIEW_VIEWED,
  SIGNUP_EVENT_BY_METHOD,
} from './analyticsEvents';

const APP_TAG = 'competitions-web';

// PII fields that mustn't leave the client — stripped before reaching GA4.
// Matches the mobile app's allowlist.
const PII_KEYS = ['name', 'email', 'phone', 'telephone', 'user_name', 'userName', 'address'];

const stripPii = (params) => {
  if (!params || typeof params !== 'object') return params;
  const out = {};
  for (const key of Object.keys(params)) {
    if (!PII_KEYS.includes(key)) out[key] = params[key];
  }
  return out;
};

// Skip analytics entirely in local dev to keep noise out of the production
// GA4 property. The hosted staging build will run with NODE_ENV=production
// so it still emits — fine for now.
const isEnabled = () => process.env.NODE_ENV === 'production';

// Non-production builds tag every event with `debug_mode: true` so the
// Firebase console's DebugView surfaces them in real time (within ~5s)
// instead of waiting the usual 24h for the Events dashboard. Strip before
// shipping to prod (or rely on NODE_ENV gating the value to false).
const IS_DEBUG_BUILD = process.env.NODE_ENV !== 'production';

const safeLogEvent = (eventName, params) => {
  if (!isEnabled()) return;
  const analytics = getAnalyticsInstance();
  if (!analytics) return; // not initialised yet / unsupported browser
  try {
    firebaseLogEvent(analytics, eventName, {
      app: APP_TAG,
      ...(IS_DEBUG_BUILD && { debug_mode: true }),
      ...stripPii(params || {}),
    });
  } catch (_e) {
    // never let an analytics error break the app
  }
};

// Single choke point: route to Firebase (PII-stripped) and Segment (full payload).
// segmentClient self-gates on env/writeKey so it's safe to call unconditionally.
const dualTrack = (eventName, params = {}) => {
  safeLogEvent(eventName, params);
  segmentTrack(eventName, params);
};

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export const identifyUser = (user) => {
  if (!user) return;
  // Firebase side (PII-stripped, gated on production)
  if (isEnabled()) {
    const analytics = getAnalyticsInstance();
    if (analytics) {
      try {
        if (user.id) setUserId(analytics, user.id);
        setUserProperties(analytics, {
          app: APP_TAG,
          ...(user.role && { user_role: user.role }),
        });
      } catch (_e) {
        // ignore
      }
    }
  }
  // Segment side (full traits, self-gated)
  if (user.id) {
    segmentIdentify(user.id, {
      ...(user.email && { email: user.email }),
      ...(user.fullName && { fullName: user.fullName }),
      ...(user.firstName && { firstName: user.firstName }),
      ...(user.secondName && { secondName: user.secondName }),
      ...(user.role && { role: user.role }),
      ...(user.gender && { gender: user.gender }),
      ...(user.country && { country: user.country }),
      ...(user.city && { city: user.city }),
    });
  }
};

// Detach identity from both surfaces — call from logout flow.
export const resetIdentity = () => {
  segmentReset();
};

// Page-view tracking (Segment only — Firebase auto-captures screen_view in web SDK).
export const trackPage = (name, properties = {}) => {
  segmentPage(name, properties);
};

// ---------------------------------------------------------------------------
// Typed event helpers
// ---------------------------------------------------------------------------

// Sign-in success. Mirrors mobile `loginSucceeded`.
export const loginSucceeded = ({ loginMethod, user, isNewUser } = {}) => {
  dualTrack(LOGIN_SUCCESS, {
    login_method: loginMethod,
    ...(isNewUser != null && { is_new_user: !!isNewUser }),
    ...(user?.id != null && { user_id: user.id }),
    event_version: 1,
  });
};

// Sign-up. Fires BOTH the per-method event (GOOGLE_REG_COMPLETED etc.) and
// the generic SIGNUP_COMPLETED, matching mobile's `registerCompleted`.
export const registerCompleted = ({ signupMethod, user, data } = {}) => {
  const params = {
    ...(data || {}),
    ...(signupMethod && { signup_method: signupMethod }),
    ...(user?.id != null && { user_id: user.id }),
    event_version: 2,
  };
  const methodEventName = SIGNUP_EVENT_BY_METHOD[signupMethod];
  if (methodEventName) dualTrack(methodEventName, params);
  dualTrack(SIGNUP_COMPLETED, params);
};

export const competitionCreated = ({ competitionId, sport, categoryCount, user } = {}) => {
  dualTrack(COMPETITION_CREATED, {
    ...(competitionId != null && { competition_id: competitionId }),
    ...(sport && { sport }),
    ...(categoryCount != null && { category_count: categoryCount }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

// Tracked when a team, admin, or referee is added to a tournament.
// participantType distinguishes the 4 flows.
export const participantAdded = ({
  participantType,
  competitionId,
  categoryId,
  user,
} = {}) => {
  dualTrack(PARTICIPANT_ADDED, {
    participant_type: participantType,
    ...(competitionId != null && { competition_id: competitionId }),
    ...(categoryId != null && { category_id: categoryId }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

// Fired when the user sets up a tournament category's format (stages /
// blocks). `templateType` distinguishes the four creation paths:
//   manualGrid          — empty phase the user wires up themselves
//   knockoutPhase       — single bracket
//   groupPhase          — groups only
//   groupStageKnockout  — groups feeding into a bracket
export const formatCreated = ({
  templateType,
  competitionId,
  categoryId,
  phaseCount,
  user,
} = {}) => {
  dualTrack(FORMAT_CREATED, {
    ...(templateType && { template_type: templateType }),
    ...(competitionId != null && { competition_id: competitionId }),
    ...(categoryId != null && { category_id: categoryId }),
    ...(phaseCount != null && { phase_count: phaseCount }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

// `generationMode` distinguishes the bulk-auto flow ('auto') from the
// single-match flow ('manual').
export const fixturesGenerated = ({
  generationMode,
  competitionId,
  categoryId,
  fixtureCount,
  user,
} = {}) => {
  dualTrack(FIXTURES_GENERATED, {
    generation_mode: generationMode,
    ...(competitionId != null && { competition_id: competitionId }),
    ...(categoryId != null && { category_id: categoryId }),
    ...(fixtureCount != null && { fixture_count: fixtureCount }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const resultRecorded = ({
  competitionId,
  fixtureId,
  homeScore,
  awayScore,
  user,
} = {}) => {
  dualTrack(RESULT_RECORDED, {
    ...(competitionId != null && { competition_id: competitionId }),
    ...(fixtureId != null && { fixture_id: fixtureId }),
    ...(homeScore != null && { home_score: homeScore }),
    ...(awayScore != null && { away_score: awayScore }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

// ---------------------------------------------------------------------------
// New helpers (web-specific events introduced with Segment integration)
// ---------------------------------------------------------------------------

export const logout = ({ user } = {}) => {
  dualTrack(LOGOUT, {
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const passwordResetRequested = ({ method } = {}) => {
  dualTrack(PASSWORD_RESET_REQUESTED, {
    ...(method && { method }),
  });
};

export const passwordResetCompleted = ({ method } = {}) => {
  dualTrack(PASSWORD_RESET_COMPLETED, {
    ...(method && { method }),
  });
};

export const organisationCreated = ({ organisationId, organisationName, user } = {}) => {
  dualTrack(ORGANISATION_CREATED, {
    ...(organisationId != null && { organisation_id: organisationId }),
    ...(organisationName && { organisation_name: organisationName }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const organisationMemberAdded = ({
  organisationId,
  invitedUserId,
  invitedEmail,
  role,
  user,
} = {}) => {
  dualTrack(ORGANISATION_MEMBER_ADDED, {
    ...(organisationId != null && { organisation_id: organisationId }),
    ...(invitedUserId != null && { invited_user_id: invitedUserId }),
    ...(invitedEmail && { invited_email: invitedEmail }),
    ...(role && { role }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const organisationMemberRemoved = ({
  organisationId,
  tournamentId,
  memberId,
  user,
} = {}) => {
  dualTrack(ORGANISATION_MEMBER_REMOVED, {
    ...(organisationId != null && { organisation_id: organisationId }),
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(memberId != null && { member_id: memberId }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const tournamentWizardStepCompleted = ({ stepName, tournamentId, user } = {}) => {
  dualTrack(TOURNAMENT_WIZARD_STEP_COMPLETED, {
    ...(stepName && { step_name: stepName }),
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const fixtureCreated = ({
  fixtureId,
  tournamentId,
  categoryId,
  isCustom,
  user,
} = {}) => {
  dualTrack(FIXTURE_CREATED, {
    ...(fixtureId != null && { fixture_id: fixtureId }),
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(categoryId != null && { category_id: categoryId }),
    ...(isCustom != null && { is_custom: !!isCustom }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const fixtureEdited = ({
  fixtureId,
  tournamentId,
  user,
} = {}) => {
  dualTrack(FIXTURE_EDITED, {
    ...(fixtureId != null && { fixture_id: fixtureId }),
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const teamRegistrationPaid = ({
  tournamentId,
  teamId,
  amount,
  paymentId,
  user,
} = {}) => {
  dualTrack(TEAM_REGISTRATION_PAID, {
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(teamId != null && { team_id: teamId }),
    ...(amount != null && { amount }),
    ...(paymentId != null && { payment_id: paymentId }),
    ...(user?.id != null && { user_id: user.id }),
  });
};

export const publicViewViewed = ({ tournamentId, subview } = {}) => {
  dualTrack(PUBLIC_VIEW_VIEWED, {
    ...(tournamentId != null && { tournament_id: tournamentId }),
    ...(subview && { subview }),
  });
};
